import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import MainApp from './pages/MainApp';
import Landing from './pages/Landing';
import POC from './pages/POC';
import StaffingLanding from './pages/staffing/StaffingLanding';
import RecruiterDashboard from './pages/staffing/RecruiterDashboard';
import CandidateView from './pages/staffing/CandidateView';
import CandidateGuard from './components/staffing/CandidateGuard';
import TalentConnectPricing from './pages/staffing/TalentConnectPricing';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import HostGuard from './components/HostGuard';
import TopNav from './components/talentConnect/TopNav';
import AgentAvatar from './components/AgentAvatar';

/**
 * Preserves ?search and #hash when redirecting legacy /staffing/* URLs.
 * Kept as an inline component so App.js remains the single source of route
 * truth (grep for "Route path" and see everything).
 */
function RedirectWithQuery({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/**
 * Phase 14 — Preserves the sub-path when redirecting /consumer/* → /signvoice/*.
 * The `*` splat token from react-router is exposed as params['*'].
 */
function RedirectConsumerSplat() {
  const params = useParams();
  const rest = params['*'] || '';
  const { search, hash } = useLocation();
  const dest = rest ? `/signvoice/${rest}` : '/signvoice';
  return <Navigate to={`${dest}${search}${hash}`} replace />;
}

/**
 * SignVoice router — Phase 14 route restructure.
 *
 * Public entry:
 *   /                      → Landing (marketing hero + 2 CTAs)
 *   /signvoice             → MainApp (real-time ASL translator — was mounted at /)
 *   /signvoice/*           → MainApp sub-paths
 *   /consumer, /consumer/* → Navigate → /signvoice, /signvoice/* (backward-compat)
 *   /poc                   → POC diagnostics
 *   /talent-connect/*      → B2B staffing surface (unchanged)
 *   /privacy, /terms       → shared legal
 *   /staffing/*            → Navigate → /talent-connect/* (Phase 8 legacy)
 *
 * TopNav is mounted at the root and appears on all surfaces except the
 * candidate iPhone-first view. On the landing route it renders with no tab
 * active. See components/talentConnect/TopNav.jsx.
 *
 * HostGuard stays dormant unless split-domain env vars are set.
 */
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <HostGuard>
          <TopNav />
          <AgentAvatar />
          <Routes>
            {/* Phase 14 — new public landing at / */}
            <Route path="/" element={<Landing />} />

            {/* SignVoice consumer app moved here from / */}
            <Route path="/signvoice" element={<MainApp />} />
            <Route path="/signvoice/*" element={<MainApp />} />

            {/* Backward-compat: /consumer* → /signvoice* */}
            <Route path="/consumer" element={<Navigate to="/signvoice" replace />} />
            <Route path="/consumer/*" element={<RedirectConsumerSplat />} />

            <Route path="/poc" element={<POC />} />

            {/* Talent Connect — TopNav visible; suppressed only on /candidate */}
            <Route path="/talent-connect" element={<StaffingLanding />} />
            <Route path="/talent-connect/recruiter" element={<RecruiterDashboard />} />
            <Route path="/talent-connect/recruiter/*" element={<RecruiterDashboard />} />
            <Route
              path="/talent-connect/candidate"
              element={
                <CandidateGuard>
                  <CandidateView />
                </CandidateGuard>
              }
            />
            <Route path="/talent-connect/pricing" element={<TalentConnectPricing />} />

            {/* Shared legal pages — TopNav visible above */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            {/* Legacy /staffing/* redirects — preserve query + hash */}
            <Route path="/staffing" element={<RedirectWithQuery to="/talent-connect" />} />
            <Route path="/staffing/recruiter" element={<RedirectWithQuery to="/talent-connect/recruiter" />} />
            <Route path="/staffing/candidate" element={<RedirectWithQuery to="/talent-connect/candidate" />} />
            <Route path="/staffing/*" element={<Navigate to="/talent-connect" replace />} />
          </Routes>
        </HostGuard>
      </BrowserRouter>
    </div>
  );
}

export default App;
